import { z } from 'zod';

/**
 * Phase 16c.1 (D-346…): which models people pick in chat. An admin opens a chat model ("Sohbette"); a discovered model
 * starts closed with its kind guessed from its name. Bireysel: each package lists its chat models (Ücretsiz exactly one,
 * Pro any of the open ones); kurumsal: an open model is for everyone or for chosen teams.
 */
export const MODEL_KINDS = ['chat', 'embedding', 'rerank', 'ocr', 'guard'] as const;
export type ModelKind = (typeof MODEL_KINDS)[number];
export const modelKind = z.enum(MODEL_KINDS);

/**
 * A discovered model's kind from its name ("adından tahmin"): rerank before embed ("bge-reranker"), "ocr" or "docling"
 * reads pages, "guard" checks safety; anything else talks.
 */
export function guessModelKind(modelId: string): ModelKind {
  const name = modelId.toLowerCase();
  if (name.includes('rerank')) return 'rerank';
  if (name.includes('embed')) return 'embedding';
  if (name.includes('ocr') || name.includes('docling')) return 'ocr';
  if (name.includes('guard')) return 'guard';
  return 'chat';
}

/** LiteLLM's own routing prefixes in `litellm_params.model` ("hosted_vllm/Qwen/…"): the model behind them is the rest */
const ROUTING_PREFIXES = ['hosted_vllm', 'vllm', 'openai', 'litellm_proxy', 'ollama', 'ollama_chat', 'huggingface', 'together_ai', 'azure', 'bedrock', 'vertex_ai', 'anthropic', 'gemini'];

function canonical(value: string): string {
  let name = value.trim().toLowerCase();
  // a served path ("/models/qwen3.8-27b") is known by its last part
  if (name.startsWith('/')) name = name.split('/').filter(Boolean).pop() ?? name;
  const slash = name.indexOf('/');
  if (slash > 0 && ROUTING_PREFIXES.includes(name.slice(0, slash))) name = name.slice(slash + 1);
  return name;
}

/** The names a model is known by: its own id, and what its provider says it serves (vLLM `root`, LiteLLM's model) */
export function servedNames(model: { modelId: string; servedAs: string | null }): string[] {
  const names = new Set([canonical(model.modelId)]);
  if (model.servedAs) names.add(canonical(model.servedAs));
  return [...names];
}

/** The same model twice: the same id, or the same served model reached through another provider (or another alias) */
export function sameServedModel(a: { modelId: string; servedAs: string | null }, b: { modelId: string; servedAs: string | null }): boolean {
  const names = new Set(servedNames(a));
  return servedNames(b).some((name) => names.has(name));
}

export type ChatAudience = 'everyone' | 'teams';

/**
 * `GET /api/models`' `chat`: what this person may pick now. `modelRefs` in the picker's order, `defaultModelRef` the one
 * a new chat starts with (bireysel: the package's; kurumsal: the Sohbet task's), `lockedModelRefs` (bireysel, Ücretsiz)
 * the Pro models shown with a lock. A model outside these is refused by the server whatever the client sends.
 */
export interface ChatChoicesDto {
  defaultModelRef: string | null;
  modelRefs: string[];
  lockedModelRefs: string[];
}
